import { __assign, __read } from "tslib";
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTooltip } from '@keen.io/react-hooks';
import Map from './map.component';
import { MapCursor } from './map.styles.';
import { Tooltip } from './components';
import { ChartBase } from '../../components';
import { generateChoropleth } from './utils';
import { useZoom, useDragHandlers } from './hooks';
import { margins as defaultMargins, theme as defaultTheme } from '../../theme';
import { THREE_DIMENSION_PROJECTIONS } from './constants';
var tooltipMotion = {
    transition: { duration: 0.3 },
    exit: { opacity: 0 },
};
var getCursorType = function (dragged, zoomDirection) {
    if (dragged) {
        return 'grabbing';
    }
    if (zoomDirection === 'zoom-in') {
        return 'zoom-in';
    }
    if (zoomDirection === 'zoom-out') {
        return 'zoom-out';
    }
    return 'grab';
};
export var ChoroplethChart = function (_a) {
    var svgDimensions = _a.svgDimensions, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, _c = _a.margins, margins = _c === void 0 ? defaultMargins : _c, labelSelector = _a.labelSelector, geoKey = _a.geoKey, colorMode = _a.colorMode, colorSteps = _a.colorSteps, topology = _a.topology, valuesRange = _a.valuesRange, _d = _a.projection, projection = _d === void 0 ? 'mercator' : _d, _e = _a.projectionScale, projectionScale = _e === void 0 ? 100 : _e, _f = _a.projectionTranslation, projectionTranslation = _f === void 0 ? [0, 0] : _f, _g = _a.projectionRotation, projectionRotation = _g === void 0 ? [0, 0, 0] : _g, _h = _a.valueKey, valueKey = _h === void 0 ? 'value' : _h, elementsKey = _a.elementsKey, data = _a.data, _j = _a.tooltipSettings, tooltipSettings = _j === void 0 ? {} : _j, onUpdateGeoMatchStatus = _a.onUpdateGeoMatchStatus;
    var svgElement = useRef(null);
    var _k = __read(useState({
        rotation: projectionRotation,
        scale: projectionScale,
        translation: projectionTranslation,
    }), 2), projectionState = _k[0], setProjectionState = _k[1];
    var _l = useTooltip(svgElement), tooltipVisible = _l.tooltipVisible, tooltipPosition = _l.tooltipPosition, tooltipMeta = _l.tooltipMeta, updateTooltipPosition = _l.updateTooltipPosition, hideTooltip = _l.hideTooltip;
    var themeTooltipSettings = theme.tooltip;
    var _m = generateChoropleth({
        topology: topology,
        projection: {
            type: projection,
            rotation: projectionState.rotation,
            translation: projectionState.translation,
            scale: projectionState.scale,
        },
        margins: margins,
        geoKey: geoKey,
        valueKey: valueKey,
        data: data,
        dimension: svgDimensions,
        colorScale: {
            colors: theme.colors,
            mode: colorMode,
            steps: colorSteps,
        },
    }), drawPath = _m.drawPath, graticule = _m.graticule, geoData = _m.geoData, geoProjection = _m.geoProjection, getColor = _m.getColor;
    var dragged = useDragHandlers(svgElement, geoProjection, setProjectionState, THREE_DIMENSION_PROJECTIONS.includes(projection) ? 'rotate' : 'translate').dragged;
    var zoomDirection = useZoom(svgElement, setProjectionState, projectionScale).zoomDirection;
    useEffect(function () {
        if (dragged && themeTooltipSettings.enabled)
            hideTooltip();
    }, [dragged]);
    return (React.createElement(React.Fragment, null,
        React.createElement(AnimatePresence, null, tooltipVisible && (React.createElement(motion.div, __assign({}, tooltipMotion, { initial: { opacity: 0, x: tooltipPosition.x, y: tooltipPosition.y }, animate: {
                x: tooltipPosition.x,
                y: tooltipPosition.y,
                opacity: 1,
            }, style: {
                position: 'absolute',
                pointerEvents: 'none',
            } }), tooltipMeta && (React.createElement(Tooltip, { mode: themeTooltipSettings.mode, formatValue: tooltipSettings.formatValue, theme: {
                labels: themeTooltipSettings.labels,
                values: themeTooltipSettings.values,
            }, partialValues: tooltipMeta.elements, totalValue: tooltipMeta.value, geographicalName: tooltipMeta.label }))))),
        topology && (React.createElement(ChartBase, { ref: svgElement, theme: theme, svgDimensions: svgDimensions, margins: margins },
            React.createElement(MapCursor, { cursor: getCursorType(dragged, zoomDirection) },
                React.createElement(Map, { topology: topology, drawPath: drawPath, graticule: graticule, getColor: getColor, labelKey: labelSelector, geoData: geoData, geoKey: geoKey, valueKey: valueKey, elementsKey: elementsKey, valuesRange: valuesRange, onUpdateGeoMatchStatus: onUpdateGeoMatchStatus, onMouseEnter: function (e, meta) {
                        if (themeTooltipSettings.enabled && !dragged) {
                            updateTooltipPosition(e, null, meta);
                        }
                    }, onMouseLeave: function () {
                        if (themeTooltipSettings.enabled)
                            hideTooltip();
                    } }))))));
};
export default ChoroplethChart;
